import { useState, useEffect, useRef } from 'react';
import { Input, Card, Row, Col, Typography, Spin } from 'antd';
import { SearchOutlined, CloseCircleFilled } from '@ant-design/icons';
import useApi from '../../../hooks/useApi';
import { API_METHODS } from '../../../constants';
import { Link, useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

const SearchComponent = ({ searchExpanded, setSearchExpanded, isTabletOrMobile }) => {
    const [searchValue, setSearchValue] = useState('');
    const [searchResults, setSearchResults] = useState();
    const [loading, setLoading] = useState(false);
    const [searchResponse, searchRequest] = useApi();
    const searchInput = useRef(null);
    const navigate = useNavigate();

    // Focus input when search is expanded
    useEffect(() => {
        if (searchExpanded && searchInput.current) {
            searchInput.current.focus();
        }
    }, [searchExpanded]);

    // Simulate API call
    const simulateApiCall = async (query) => {
        setLoading(true);
        searchRequest(
            null,
            API_METHODS.GET,
            `/website/home/search?search_text=${query}`
        );
    };

    // Handle search input changes
    useEffect(() => {
        if (searchValue.length >= 3) {
            simulateApiCall(searchValue);
        } else if (searchValue.length === 0) {
            setSearchResults({});
        }
    }, [searchValue]);

    // Handle search response
    useEffect(() => {
        if (searchResponse.data && !searchResponse.isLoading) {
            setSearchResults(searchResponse.data);
            setLoading(false);
        } else if (searchValue.length === 0) {
            setSearchResults({});
        }
    }, [searchResponse]);

    const handleInputBlur = () => {
        if (!searchValue) {
            setSearchExpanded(false);
        }
    };

    const handleClearSearch = () => {
        setSearchValue('');
        setSearchResults({});
        setSearchExpanded(false);
    };

    // Handle Enter key press
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && searchValue.length >= 3) {
            // Navigate to search results page
            navigate(`/search?q=${encodeURIComponent(searchValue)}`, { state: { "searchQuery": searchValue, "searchResults": searchResults } });
            setSearchExpanded(false);
        }
    };

    const CategoryCard = ({ category }) => (
        <Col span={isTabletOrMobile ? 12 : 6} key={category.id}>
            <Link to={category.category_url} style={{ textDecoration: "none" }} target='_blank' key={category.id}>
                <Card
                    hoverable
                    className="text-center"
                    style={{
                        borderRadius: 12,
                        border: '1px solid #f0f0f0',
                        height: "auto",
                        minWidth: isTabletOrMobile ? 80 : 110
                    }}
                    bodyStyle={{ padding: isTabletOrMobile ? '12px 6px' : '16px 8px' }}
                >
                    <img style={{
                        fontSize: 32,
                        marginBottom: 8,
                        width: isTabletOrMobile ? 60 : 100,
                        height: isTabletOrMobile ? 60 : 100,
                    }} src={category.image} alt={category.category} />
                    <Text style={{ fontSize: isTabletOrMobile ? 11 : 12, fontWeight: "bold", color: '#333' }}>
                        {category.category}
                    </Text>
                </Card>
            </Link>
        </Col>
    );

    const ProductCard = ({ product }) => (
        <div style={{ marginBottom: 16 }}>
            <Link to={product.product_url} style={{ textDecoration: "none" }} target='_blank' key={product.id}>
                <Card
                    hoverable
                    size="small"
                    style={{ borderRadius: 8, border: '1px solid #f0f0f0' }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <img style={{
                            width: isTabletOrMobile ? 40 : 48,
                            height: isTabletOrMobile ? 40 : 48,
                            backgroundColor: '#f5f5f5',
                            borderRadius: 6,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 20
                        }} src={product.image} alt={product.product_name} />
                        <div style={{ flex: 1 }}>
                            <Text strong style={{ fontSize: isTabletOrMobile ? 13 : 14, color: '#333' }}>
                                {product.product_name}
                            </Text>
                            <br />
                            <Text style={{ fontSize: isTabletOrMobile ? 11 : 12, color: '#666' }}>
                                {product.product_name}
                            </Text>
                        </div>
                    </div>
                </Card>
            </Link>
        </div>
    );

    if (!searchExpanded) return null;

    return (
        <div style={{
            position: 'fixed',
            top: isTabletOrMobile ? 60 : 70,
            right: isTabletOrMobile ? 10 : 30,
            left: isTabletOrMobile ? 20 : 'auto',
            width: isTabletOrMobile ? 'calc(100% - 82px)' : 600,
            maxWidth: isTabletOrMobile ? '100%' : 600,
            padding: "20px",
            backgroundColor: '#ffffff',
            maxHeight: isTabletOrMobile ? 'calc(100vh - 80px)' : '600px',
            zIndex: 1000,
            overflowY: searchResults?.products ? "scroll" : "auto",
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            borderRadius: 8
        }}>
            {/* Search Input */}
            <div style={{
                marginBottom: 20,
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
                paddingBottom: 10,
                zIndex: 10
            }}>
                <Input
                    ref={searchInput}
                    placeholder="Search for categories or products..."
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onBlur={handleInputBlur}
                    onKeyPress={handleKeyPress}
                    prefix={<SearchOutlined />}
                    suffix={
                        searchValue && (
                            <CloseCircleFilled
                                onClick={handleClearSearch}
                                onMouseDown={(e) => e.preventDefault()} // Prevent blur when clicking
                                style={{
                                    cursor: 'pointer',
                                    color: '#999',
                                    fontSize: 16
                                }}
                            />
                        )
                    }
                    style={{
                        borderRadius: 20,
                        backgroundColor: 'white',
                        border: '1px solid #d9d9d9',
                        height: 40
                    }}
                />
                {searchValue.length >= 3 && (
                    <Text style={{
                        fontSize: 11,
                        color: '#999',
                        marginTop: 4,
                        display: 'block'
                    }}>
                        Press Enter to see all results
                    </Text>
                )}
            </div>

            {loading && (
                <div style={{ textAlign: 'center', margin: '20px 0' }}>
                    <Spin size="large" />
                </div>
            )}

            {!loading && searchResults?.products && searchValue !== '' && (
                <>
                    {/* Categories Section */}
                    <div style={{ marginBottom: 32 }}>
                        <Title level={4} style={{ marginBottom: 16, color: '#333', fontSize: isTabletOrMobile ? 16 : 18 }}>
                            Categories
                        </Title>
                        {searchResults.categories?.length !== 0 ? (
                            <Row gutter={[12, 12]}>
                                {searchResults.categories?.map(category => (
                                    <CategoryCard key={category.id} category={category} />
                                ))}
                            </Row>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                <Text style={{ color: '#666' }}>No category found related to "{searchValue}"</Text>
                            </div>
                        )}
                    </div>

                    {/* Products Section */}
                    <div>
                        <Title level={4} style={{ marginBottom: 16, color: '#333', fontSize: isTabletOrMobile ? 16 : 18 }}>
                            Products
                        </Title>
                        {searchResults.products?.length !== 0 ? (
                            <div>
                                {searchResults.products?.map(product => (
                                    <ProductCard key={product.id} product={product} />
                                ))}
                            </div>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                <Text style={{ color: '#666' }}>No products found for "{searchValue}"</Text>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default SearchComponent;